// useBucketSummary — the one place that decides whether a board's segmentation
// is worth a request at all.
//
// Both tabs that show segmentation need the same aggregate for the same cycle,
// and both must stay silent on boards where it has nothing to say: on
// `segmentation=none` there are no segments, and on a tier board with rooms off
// the segment count is already in `tier_config`. Keeping that condition here
// rather than at each call site is what keeps a future third caller from
// quietly firing a request on every board.

import { useQuery } from "@tanstack/react-query";

import { getBucketSummary, lbKeys } from "./api";
import { needsBucketSummary } from "./segments";
import type { BucketSummary, Leaderboard } from "./types";

export function useBucketSummary(
  gameId: string,
  lbId: string,
  cycleId: string | undefined,
  lb: Pick<Leaderboard, "segmentation" | "bucket_size">,
): BucketSummary | undefined {
  const q = useQuery({
    queryKey: lbKeys.bucketSummary(gameId, lbId, cycleId ?? ""),
    queryFn: () => {
      if (!cycleId) return Promise.reject(new Error("no cycle"));
      return getBucketSummary(gameId, lbId, cycleId);
    },
    enabled: !!gameId && !!lbId && !!cycleId && needsBucketSummary(lb),
  });
  // Deliberately no error surface: the aggregate is a caption, not the screen.
  // A failed request leaves every caller with `undefined`, which each of them
  // already renders as "counts unknown" rather than as a broken card.
  return q.data;
}
